import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Box,
  CircularProgress,
  Stack,
  Button,
  Icon,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useState } from "react";
import { LogDetailsRequest } from "../../../services/Endpoints/logs/LogDetailsEndpoint";
import errorResolver from "../../../utils/error-resolver";
import { useNotificationContext } from "../../../context/notificationContext";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { ILogDetailsSchema } from "../../../services/Schemas/Logs";
import { dateFormatter } from "../../../utils/date-formatter";
import { CopyToClipboard } from "react-copy-to-clipboard";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import UserLink from "../common/UserLink";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { useUrl } from "../../../hooks/urlEncoding";
import { routerPaths } from "../../../constants/Paths";
import { WorkspaceListColumnFilterable } from "../../../services/Endpoints/workspaces";
import { FilterOperatorEnum } from "../../../services/Schemas/ListQueryString";
import { UsersListColumnFilterable } from "../../../services/Endpoints/users/UsersListEndpoint";
import { copyToClipboard } from "../../../utils/copy-to-clipboard";

interface Props {
  open: boolean;
  onClose: () => void;
  logId: string | undefined;
}

const safeParseJSON = (str: string) => {
  try {
    return JSON.parse(str);
  } catch {
    return { raw: str };
  }
};

const renderJSONTree = (json: any, prefix = "") =>
  Object.entries(json).map(([key, value], index) => {
    const nodeId = `${prefix}-${key}-${index}`;
    if (typeof value === "object" && value !== null) {
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          label={
            <Box sx={{ fontFamily: "monospace", fontSize: 13 }}>
              <span style={{ color: "#1976d2", userSelect: "text" }}>
                {key}
              </span>
            </Box>
          }
        >
          {renderJSONTree(value, nodeId)}
        </TreeItem>
      );
    } else {
      return (
        <TreeItem
          key={nodeId}
          nodeId={nodeId}
          label={
            <Box sx={{ fontFamily: "monospace", fontSize: 13 }}>
              <span style={{ color: "#1976d2", userSelect: "text" }}>
                {key}
              </span>
              <span style={{ color: "#333", userSelect: "text" }}>{`: ${String(
                value,
              )}`}</span>
            </Box>
          }
        />
      );
    }
  });

const renderRequestTree = (
  raw: string,
  copiedMap: Record<string, boolean>,
  setCopiedMap: React.Dispatch<React.SetStateAction<{}>>,
) => {
  const parsed = safeParseJSON(raw);
  let headers: Record<string, any> = {};
  let bodyParsed: Record<string, any> | null = null;

  if (parsed.headers && typeof parsed.headers === "object") {
    headers = parsed.headers;
  } else {
    headers = { ...parsed };
    delete headers.body;
  }

  if (parsed.body) {
    if (typeof parsed.body === "string") {
      try {
        bodyParsed = JSON.parse(parsed.body);
      } catch {
        bodyParsed = { raw: parsed.body };
      }
    } else if (typeof parsed.body === "object") {
      bodyParsed = parsed.body;
    } else {
      bodyParsed = { raw: String(parsed.body) };
    }
  }

  const handleCopy = (id: string, value: string) => {
    copyToClipboard(value);

    setCopiedMap((prev) => ({ ...prev, [id]: true }));

    setTimeout(() => {
      setCopiedMap((prev) => ({ ...prev, [id]: false }));
    }, 1000);
  };

  return (
    <TreeView defaultExpandIcon="▶" defaultCollapseIcon="▼">
      <TreeItem nodeId="headers" label="Headers">
        {Object.entries(headers).map(([key, value], i) => {
          const nodeId = `header-${i}`;

          return (
            <TreeItem
              key={key}
              nodeId={nodeId}
              label={
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 14,
                  }}
                >
                  <span
                    style={{
                      userSelect: "auto",
                      cursor: "pointer",
                      color: "#333",
                    }}
                  >
                    <span>
                      <Tooltip title={copiedMap[nodeId] ? " ✔ کپی شد" : "کپی "}>
                        <span
                          onClick={() => handleCopy(nodeId, key)}
                          className="clipboardItem"
                          style={{ color: "#1976d2", userSelect: "text" }}
                        >
                          {key}
                        </span>
                      </Tooltip>
                      <Tooltip title={copiedMap[nodeId] ? " ✔ کپی شد" : "کپی "}>
                        <span
                          onClick={() => handleCopy(nodeId, String(value))}
                          className="clipboardItem"
                        >
                          {`: ${String(value)}`}
                        </span>
                      </Tooltip>
                    </span>
                  </span>
                </Box>
              }
            />
          );
        })}
      </TreeItem>

      {bodyParsed && (
        <TreeItem nodeId="body" label="Body">
          {renderJSONTree(bodyParsed)}
        </TreeItem>
      )}
    </TreeView>
  );
};

const renderResponseTree = (
  raw: string,
  copiedMap: Record<string, boolean>,
  setCopiedMap: React.Dispatch<React.SetStateAction<{}>>,
) => {
  const parsed = safeParseJSON(raw);
  let headers: Record<string, any> = {};
  let bodyParsed: Record<string, any> | null = null;

  if (parsed.headers && typeof parsed.headers === "object") {
    headers = parsed.headers;
  } else {
    headers = { ...parsed };
    delete headers.body;
  }

  if (parsed.body) {
    if (typeof parsed.body === "string") {
      try {
        bodyParsed = JSON.parse(parsed.body);
      } catch {
        bodyParsed = { raw: parsed.body };
      }
    } else if (typeof parsed.body === "object") {
      bodyParsed = parsed.body;
    } else {
      bodyParsed = { raw: String(parsed.body) };
    }
  }
  const handleCopy = (id: string, value: string) => {
    copyToClipboard(value);

    setCopiedMap((prev) => ({ ...prev, [id]: true }));

    setTimeout(() => {
      setCopiedMap((prev) => ({ ...prev, [id]: false }));
    }, 1000);
  };
  return (
    <TreeView defaultExpandIcon="▶" defaultCollapseIcon="▼">
      <TreeItem nodeId="response-headers" label="Headers">
        {Object.entries(headers).map(([key, value], i) => {
          const nodeId = `response-header-${i}`;
          return (
            <TreeItem
              key={key}
              nodeId={nodeId}
              label={
                <Box
                  sx={{
                    fontFamily: "monospace",
                    fontSize: 14,
                  }}
                >
                  <span
                    style={{
                      userSelect: "auto",
                      cursor: "pointer",
                      color: "#333",
                    }}
                  >
                    <span>
                      <Tooltip title={copiedMap[nodeId] ? " ✔ کپی شد" : "کپی "}>
                        <span
                          onClick={() => handleCopy(nodeId, key)}
                          className="clipboardItem"
                          style={{ color: "#1976d2", userSelect: "text" }}
                        >
                          {key}
                        </span>
                      </Tooltip>
                      <Tooltip title={copiedMap[nodeId] ? " ✔ کپی شد" : "کپی "}>
                        <span
                          onClick={() => handleCopy(nodeId, String(value))}
                          className="clipboardItem"
                        >
                          {`: ${String(value)}`}
                        </span>
                      </Tooltip>
                    </span>
                  </span>
                </Box>
              }
            />
          );
        })}
      </TreeItem>
      {bodyParsed && (
        <TreeItem nodeId="response-body" label="Body">
          {renderJSONTree(bodyParsed, "response-body")}
        </TreeItem>
      )}
    </TreeView>
  );
};

const extractBody = (raw: string): string => {
  const parsed = safeParseJSON(raw);
  if (typeof parsed.body === "string") return parsed.body;
  if (parsed.body) return JSON.stringify(parsed.body, null, 2);
  return "-";
};

export default function LogDetailsModal({ open, onClose, logId }: Props) {
  const [copiedMap, setCopiedMap] = useState({});
  const [data, setData] = useState<ILogDetailsSchema | null>(null);
  const [loading, setLoading] = useState(false);
  const { setNotification } = useNotificationContext();
  const axios = useProtectedAxiosInstance();
  const { can } = useCanAccess();
  const { queryStringUrl } = useUrl();

  const fetchData = async () => {
    if (!logId) return;
    setLoading(true);
    setData(null);
    try {
      const request = new LogDetailsRequest(axios);
      const { data } = await request.send({ logId });
      setData(data);
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchData();
  }, [logId, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ width: "100%", display: "flex", justifyContent: "end" }}
      >
        جزئیات لاگ #{logId}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", left: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box textAlign="center" p={4}>
            <CircularProgress />
            <Typography margin="auto" mt={4} variant="h6">
              در حال دریافت اطلاعات
            </Typography>
          </Box>
        ) : data ? (
          <>
            <Card
              sx={{
                mb: 3,
                direction: "rtl",
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  {/* آدرس و متد */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">آدرس درخواست:</Typography>
                    </Box>
                    <Typography sx={{ ml: 4, userSelect: "text" }}>
                      {data.log.url}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">متد:</Typography>
                    </Box>
                    <Typography sx={{ ml: 4 }}>
                      {data.log.request_method.toUpperCase()}
                    </Typography>
                  </Grid>

                  {/* تاریخ درخواست */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">تاریخ درخواست:</Typography>
                    </Box>
                    <Typography sx={{ ml: 4 }}>
                      {dateFormatter({ date: data.log.created_at, lang: "fa" })}
                    </Typography>
                  </Grid>

                  {/* کد پیگیری */}
                  {data.log.track_code && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight="bold">کد پیگیری:</Typography>
                      </Box>
                      <Typography sx={{ ml: 4 }}>
                        <UserLink
                          disabled={!can(AccessEnums.AdminLogsList)}
                          user={{ id: data.log.track_code }}
                          link={queryStringUrl(routerPaths.LOGS, {
                            filters: {
                              track_code: {
                                column: "track_code",
                                value: data.log.track_code,
                                operator: FilterOperatorEnum.EQUAL,
                              },
                            },
                          })}
                        />
                      </Typography>
                    </Grid>
                  )}

                  {/* کد وضعیت */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">کد وضعیت:</Typography>
                    </Box>
                    <Typography sx={{ ml: 4 }}>
                      {data.log.response_code}
                    </Typography>
                  </Grid>

                  {/* مدت زمان */}
                  <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">مدت زمان:</Typography>
                    </Box>
                    <Typography sx={{ ml: 4 }}>
                      {data.log.duration_millisecond} ms
                    </Typography>
                  </Grid>

                  {/* کاربر */}
                  {data.log.user && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">نوع:</Typography>
                        </Box>
                        <Typography sx={{ ml: 4 }}>کاربر</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">
                            شماره موبایل:
                          </Typography>
                        </Box>
                        <Typography sx={{ ml: 4 }}>
                          <UserLink
                            disabled={!can(AccessEnums.AdminUsersList)}
                            user={{ id: data.log.user.mobile }}
                            link={queryStringUrl(routerPaths.USERS, {
                              filters: {
                                [UsersListColumnFilterable.ID]: {
                                  column: UsersListColumnFilterable.ID,
                                  value: data.log.user.id,
                                  operator: FilterOperatorEnum.EQUAL,
                                },
                              },
                            })}
                          />
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {/* شریک */}
                  {data.log.partner && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">نوع:</Typography>
                        </Box>
                        <Typography sx={{ ml: 4 }}>شریک</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">نام شریک:</Typography>
                        </Box>
                        <Typography sx={{ ml: 4 }}>
                          <UserLink
                            disabled={!can(AccessEnums.AdminPartnerList)}
                            user={{ id: data.log.partner.caption }}
                            link={queryStringUrl(routerPaths.PARTNERS, {
                              filters: {
                                [WorkspaceListColumnFilterable.ID]: {
                                  column: WorkspaceListColumnFilterable.ID,
                                  value: data.log.partner.id,
                                  operator: FilterOperatorEnum.EQUAL,
                                },
                              },
                            })}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">پشتیبانی:</Typography>
                        </Box>
                        <Typography sx={{ ml: 4 }}>
                          {data.log.partner.support_fullname} (
                          {data.log.partner.support_phone})
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {/* ورک‌اسپیس */}
                  {data.log.workspace && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight="bold">ورک‌اسپیس:</Typography>
                      </Box>
                      <Typography sx={{ ml: 4 }}>
                        <UserLink
                          disabled={!can(AccessEnums.AdminWorkspacesList)}
                          user={{ id: data.log.workspace.name }}
                          link={queryStringUrl(routerPaths.WORKSPACES, {
                            filters: {
                              [WorkspaceListColumnFilterable.ID]: {
                                column: WorkspaceListColumnFilterable.ID,
                                value: data.log.workspace.id,
                                operator: FilterOperatorEnum.EQUAL,
                              },
                            },
                          })}
                        />
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Query String
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                direction: "ltr",
                userSelect: "text",
              }}
            >
              {data.log_details.query_string || "-"}
            </Typography>
            <Box mt={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Request</Typography>
                <CopyToClipboard text={extractBody(data.log_details.request)}>
                  <Tooltip title="کپی بادی">
                    <IconButton>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CopyToClipboard>
              </Box>
              {renderRequestTree(
                data.log_details.request,
                copiedMap,
                setCopiedMap,
              )}
            </Box>

            <Box mt={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Response</Typography>
                <CopyToClipboard text={extractBody(data.log_details.response)}>
                  <Tooltip title="کپی بادی">
                    <IconButton>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CopyToClipboard>
              </Box>
              {renderResponseTree(
                data.log_details.response,
                copiedMap,
                setCopiedMap,
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1">
            اطلاعاتی یافت نشد. <Button onClick={fetchData}>تلاش دوباره</Button>
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
